// Local (browser) multi-project registry.
//
// Projects live entirely in localStorage so the tool keeps working offline and
// without login. We keep a small INDEX ([{id,name,updatedAt}]) for the switcher
// list plus one key per project holding the full document. Storing docs under
// their own keys avoids rewriting every project on each autosave.
//
// The zustand store still keeps the ACTIVE project as its live `doc` (persisted
// as the working copy); this registry mirrors it and holds all other projects.

const INDEX_KEY = 'switchr:projects:index';
const DOC_PREFIX = 'switchr:projects:doc:';

const docKey = (id) => `${DOC_PREFIX}${id}`;

function uid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return 'p-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

function readIndex() {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((e) => e && e.id) : [];
  } catch {
    return [];
  }
}

function writeIndex(arr) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(arr));
  } catch {
    /* quota / disabled storage: ignore */
  }
}

export function newProjectId() {
  return uid();
}

// Projects sorted by most-recently-updated first.
export function listProjects() {
  return readIndex()
    .slice()
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

// Persist a project's doc + refresh its index entry (name + updatedAt).
export function saveProject(id, doc, updatedAt = Date.now()) {
  if (!id || !doc) return;
  try {
    localStorage.setItem(docKey(id), JSON.stringify(doc));
  } catch {
    return; // out of space: skip silently
  }
  const idx = readIndex();
  const name = (doc.name || '').trim() || 'Unbenanntes Projekt';
  const existing = idx.find((e) => e.id === id);
  if (existing) {
    existing.name = name;
    existing.updatedAt = updatedAt;
  } else {
    idx.push({ id, name, updatedAt });
  }
  writeIndex(idx);
}

export function loadProjectDoc(id) {
  try {
    const raw = localStorage.getItem(docKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function deleteProject(id) {
  try {
    localStorage.removeItem(docKey(id));
  } catch {}
  writeIndex(readIndex().filter((e) => e.id !== id));
}

export function projectExists(id) {
  return readIndex().some((e) => e.id === id);
}
